import { openDB, DBSchema, IDBPDatabase } from "idb";

const DB_NAME = "FileStorageDB";
const STORE_NAME = "files";
const DB_VERSION = 2;

export interface StoredFile {
  id: string;
  needId: string;
  name: string;
  type: string;
  size: number;
  data: ArrayBuffer;
  documentId?: string;
  uploading?: boolean;
  createdAt: number;
}

interface FileDB extends DBSchema {
  files: {
    key: string;      
    value: StoredFile;  
    indexes: {
      needId: string;
    };
  };
}


export const shareFiles = async (shareData: ShareData) => {
  if (navigator.canShare?.({ files: shareData.files })) {
    try {
      await navigator.share(shareData);
      console.log("Share was successful.");
    } catch (error) {
      console.error("Sharing failed:", error);
    }
  } else {
    console.log(`Your system doesn't support sharing files.`);
  }
};

async function getDB(): Promise<IDBPDatabase<FileDB>> {
  return openDB<FileDB>(DB_NAME, DB_VERSION, {
    upgrade(db, oldVersion) {
      if (oldVersion < 2) {
        if (db.objectStoreNames.contains(STORE_NAME)) {
          db.deleteObjectStore(STORE_NAME);
        }
      }

      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: "id" });
        store.createIndex("needId", "needId", { unique: false });
      }
    }
  });
}

export async function saveFile(needId: string, file: File): Promise<void> {
  const db = await getDB();

  const data = await file.arrayBuffer();

  const id = `${needId}__${file.name.trim().toLowerCase()}`;

  const storedFile: StoredFile = {
    id,
    needId,
    name: file.name,
    type: file.type,
    size: file.size,
    data,
    createdAt: Date.now()
  };

  await db.put(STORE_NAME, storedFile);
}


export async function getFiles(needId: string): Promise<StoredFile[]> {
  const db = await getDB();
  return db.getAllFromIndex(STORE_NAME, "needId", needId);
}


export async function deleteSingleFile(
  needId: string,
  fileName: string
): Promise<void> {
  const db = await getDB();
  const normalized = fileName.trim().toLowerCase();

  const files = await getFiles(needId);
  const match = files.find((f) => f.name.trim().toLowerCase() === normalized);

  if (!match) {
    console.warn(`File not found: ${fileName} for needId: ${needId}`);
    return;
  }

  await db.delete(STORE_NAME, match.id);
}


export async function deleteFiles(needId: string): Promise<void> {
  const db = await getDB();
  const files = await getFiles(needId);

  for (const f of files) {
    await db.delete(STORE_NAME, f.id);
  }
}


export async function deleteAllFiles(): Promise<void> {
  const db = await getDB();
  await db.clear(STORE_NAME);
}

export async function updateStoredFileDocumentId(
  needId: string,
  fileName: string,
  documentId: string
): Promise<void> {
  const db = await getDB();
  const normalized = fileName.trim().toLowerCase();

  const files = await getFiles(needId);
  const match = files.find((f) => f.name.trim().toLowerCase() === normalized);

  if (!match) {
    console.warn(`File not found for update: ${fileName} for needId: ${needId}`);
    return;
  }

  const updated: StoredFile = { ...match, documentId, uploading: false };

  await db.put(STORE_NAME, updated);
}

export async function markFileUploading(
  needId: string,
  fileName: string,
  uploading: boolean
): Promise<void> {
  const db = await getDB();
  const normalized = fileName.trim().toLowerCase();

  const files = await getFiles(needId);
  const match = files.find((f) => f.name.trim().toLowerCase() === normalized);

  if (!match) return;

  const updated: StoredFile = { ...match, uploading };
  await db.put(STORE_NAME, updated);
}
